import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { BurndownChart } from "@/components/BurndownChart";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Target, TrendingDown, AlertTriangle, CheckCircle2, Plus, ChevronDown, ChevronRight,
  Circle, CheckCircle, Clock, User, Trash2,
} from "lucide-react";

const STATUS_STYLES = {
  open: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Merge a plan's planned (2-point) + actual (history) into one chart series.
const mergePlanBurndown = (bd) => {
  if (!bd) return [];
  const { planned = [], actual = [] } = bd;
  if (!planned.length) return [];
  const start = planned[0];
  const end = planned[planned.length - 1];
  const dates = Array.from(new Set([...planned.map((p) => p.date), ...actual.map((a) => a.date)])).sort();
  const t0 = new Date(start.date).getTime();
  const t1 = new Date(end.date).getTime();
  const span = Math.max(1, t1 - t0);
  return dates.map((date) => {
    const t = new Date(date).getTime();
    const frac = Math.min(1, Math.max(0, (t - t0) / span));
    const planVal = +(start.planned - frac * (start.planned - end.planned)).toFixed(1);
    const past = actual.filter((a) => a.date <= date);
    const actualVal = past.length ? past[past.length - 1].actual : null;
    return { date, planned: planVal, actual: actualVal };
  });
};

const KpiTile = ({ icon: Icon, label, value, tone = "text-ek-purple", testid }) => (
  <div data-testid={testid} className="rounded-md border border-black/5 bg-white px-5 py-4">
    <div className="flex items-center gap-2 text-ek-ink/65">
      <Icon size={15} className={tone} />
      <span className="text-[0.7rem] font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className={`mt-2 text-3xl font-bold ${tone}`}>{value}</p>
  </div>
);

const ProgressBar = ({ baseline, current, target }) => {
  const span = baseline - target;
  const pct = span > 0 ? Math.max(0, Math.min(100, ((baseline - current) / span) * 100)) : 100;
  return (
    <div>
      <div className="flex justify-between text-[0.7rem] text-ek-ink/65">
        <span>Baseline {baseline}</span>
        <span className="font-semibold text-ek-purple">Now {current}</span>
        <span>Target {target}</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-black/5">
        <div className="h-full rounded-full bg-ek-purple transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ActionRow = ({ action, canWrite, onToggle, busy }) => {
  const done = action.status === "done";
  return (
    <div data-testid="remediation-action-row"
      className="flex items-center justify-between gap-3 rounded-md border border-black/5 bg-white px-3 py-2">
      <div className="flex items-start gap-2.5">
        <button
          disabled={!canWrite || busy}
          onClick={() => onToggle(action, done ? "todo" : "done")}
          data-testid="toggle-action-button"
          className={`mt-0.5 transition-colors ${done ? "text-emerald-600" : "text-ek-ink/30 hover:text-ek-purple"} ${canWrite ? "" : "cursor-default"}`}
        >
          {done ? <CheckCircle size={17} /> : <Circle size={17} />}
        </button>
        <div>
          <p className={`text-sm ${done ? "text-ek-ink/65 line-through" : "text-ek-ink"}`}>{action.title}</p>
          <p className="text-[0.7rem] text-ek-ink/65">
            <User size={10} className="mr-1 inline" />{action.owner_name} · due {fmtDate(action.due_date)}
            {action.residual_impact > 0 && <span className="ml-1 text-ek-purple">· −{action.residual_impact} pts</span>}
          </p>
        </div>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold uppercase ${STATUS_STYLES[action.status] || STATUS_STYLES.open}`}>
        {action.status.replace("_", " ")}
      </span>
    </div>
  );
};

const PlanCard = ({ plan, canWrite, owners, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", owner_user_id: plan.owner_user_id, due_date: plan.due_date, residual_impact: 1 });

  const loadDetail = useCallback(() => {
    api.get(`/remediation/plans/${plan.plan_id}`).then(({ data }) => setDetail(data)).catch(() => {});
  }, [plan.plan_id]);

  const expand = () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) loadDetail();
  };

  const toggleAction = async (action, status) => {
    setBusy(true);
    try {
      await api.patch(`/remediation/plans/${plan.plan_id}/actions/${action.action_id}`, { status });
      toast.success(status === "done" ? "Action completed — residual updated" : "Action reopened");
      loadDetail();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addAction = async () => {
    if (form.title.trim().length < 3) { toast.error("Title must be at least 3 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/remediation/plans/${plan.plan_id}/actions`, {
        title: form.title, owner_user_id: form.owner_user_id, due_date: form.due_date,
        residual_impact: Number(form.residual_impact) || 0,
      });
      toast.success("Action added");
      setAdding(false);
      setForm({ ...form, title: "" });
      loadDetail();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removePlan = async () => {
    if (!window.confirm("Delete this remediation plan?")) return;
    try {
      await api.delete(`/remediation/plans/${plan.plan_id}`);
      toast.success("Plan deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const d = detail?.plan || plan;
  const series = mergePlanBurndown(detail?.burndown);

  return (
    <div data-testid="remediation-plan-card" className="rounded-md border border-black/5 bg-white">
      <button onClick={expand} data-testid="expand-plan-button"
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
        <div className="flex items-start gap-3">
          {open ? <ChevronDown size={18} className="mt-1 text-ek-ink/65" /> : <ChevronRight size={18} className="mt-1 text-ek-ink/65" />}
          <div>
            <p className="font-medium text-ek-ink">{d.risk_title}</p>
            <p className="text-xs text-ek-ink/65">
              <User size={11} className="mr-1 inline" />{d.owner_name} · due {fmtDate(d.due_date)} ·
              {" "}{d.action_done}/{d.action_total} actions
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {d.overdue && (
            <span data-testid="plan-overdue-badge" className="rounded-full bg-red-100 px-2 py-0.5 text-[0.65rem] font-semibold uppercase text-red-700">
              Overdue
            </span>
          )}
          <span data-testid={`plan-status-${d.status}`} className={`rounded-full px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase ${STATUS_STYLES[d.status]}`}>
            {d.status.replace("_", " ")}
          </span>
        </div>
      </button>

      <div className="px-5 pb-4">
        <ProgressBar baseline={d.baseline_residual} current={d.current_residual} target={d.target_residual} />
      </div>

      {open && (
        <div className="border-t border-black/5 px-5 py-4">
          {d.description && <p className="mb-4 text-sm text-ek-ink/60">{d.description}</p>}
          {series.length > 0 && (
            <div className="mb-5 rounded-md bg-ek-canvas/60 p-3">
              <p className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-ek-ink/65">Risk burn-down</p>
              <BurndownChart series={series} target={d.target_residual} height={200} />
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ek-ink/65">Actions</p>
            {canWrite && (
              <button onClick={() => setAdding((s) => !s)} data-testid="add-action-toggle"
                className="flex items-center gap-1 text-xs font-semibold text-ek-purple hover:text-ek-lilac">
                <Plus size={13} /> Add action
              </button>
            )}
          </div>

          {adding && (
            <div className="mt-2 space-y-2 rounded-md border border-ek-purple/20 bg-ek-purple/5 p-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Action title" data-testid="action-title-input"
                className="w-full rounded-md border border-black/10 bg-white px-3 py-1.5 text-sm outline-none focus:border-ek-purple" />
              <div className="grid grid-cols-3 gap-2">
                <Select value={form.owner_user_id} onValueChange={(v) => setForm({ ...form, owner_user_id: v })}>
                  <SelectTrigger data-testid="action-owner-select" className="text-xs"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>
                    {owners.map((o) => <SelectItem key={o.user_id} value={o.user_id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="action-due-input"
                  className="rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs outline-none focus:border-ek-purple" />
                <input type="number" min="0" max="24" value={form.residual_impact}
                  onChange={(e) => setForm({ ...form, residual_impact: e.target.value })}
                  placeholder="− pts" data-testid="action-impact-input"
                  className="rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs outline-none focus:border-ek-purple" />
              </div>
              <button onClick={addAction} disabled={busy} data-testid="save-action-button"
                className="ek-btn w-full py-1.5 text-xs">Save action</button>
            </div>
          )}

          <div className="mt-3 space-y-2">
            {(d.actions || []).length === 0 && <p className="text-xs text-ek-ink/65">No actions yet.</p>}
            {(d.actions || []).map((a) => (
              <ActionRow key={a.action_id} action={a} canWrite={canWrite} onToggle={toggleAction} busy={busy} />
            ))}
          </div>

          {canWrite && (
            <button onClick={removePlan} data-testid="delete-plan-button"
              className="mt-4 flex items-center gap-1 text-xs text-ek-ink/65 hover:text-red-600">
              <Trash2 size={12} /> Delete plan
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const RiskRemediation = () => {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "risk.write");
  const [data, setData] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [owners, setOwners] = useState([]);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);

  // create dialog
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ risk_id: "", owner_user_id: "", target_residual: "", due_date: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    return Promise.all([
      api.get("/remediation/plans"),
      api.get("/remediation/burndown"),
    ])
      .then(([p, b]) => { setData(p.data); setBurndown(b.data); })
      .catch(() => toast.error("Failed to load remediation plans"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    if (canWrite) {
      api.get("/remediation/owners").then(({ data }) => setOwners(data.owners)).catch(() => {});
      api.get("/risks").then(({ data }) => setRisks(data.items)).catch(() => {});
    }
  }, [load, canWrite]);

  const selectedRisk = risks.find((r) => r.risk_id === form.risk_id);

  const submit = async () => {
    if (!form.risk_id || !form.owner_user_id || !form.target_residual || !form.due_date) {
      toast.error("Risk, owner, target and due date are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/remediation/plans", {
        risk_id: form.risk_id, owner_user_id: form.owner_user_id,
        target_residual: Number(form.target_residual), due_date: form.due_date,
        description: form.description,
      });
      toast.success("Remediation plan created");
      setCreating(false);
      setForm({ risk_id: "", owner_user_id: "", target_residual: "", due_date: "", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const s = data?.summary || {};
  const aggSeries = burndown?.series || [];

  return (
    <div data-testid="risk-remediation">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiTile testid="kpi-open-plans" icon={Target} label="Open plans" value={s.open ?? 0} />
        <KpiTile testid="kpi-overdue" icon={AlertTriangle} label="Overdue" value={s.overdue ?? 0} tone="text-red-600" />
        <KpiTile testid="kpi-completed" icon={CheckCircle2} label="Completed" value={s.completed ?? 0} tone="text-emerald-600" />
        <KpiTile testid="kpi-reduced" icon={TrendingDown} label="Residual reduced" value={s.residual_reduced ?? 0} tone="text-ek-purple" />
      </div>

      {/* Aggregate burn-down */}
      {aggSeries.length > 0 && (
        <div className="mt-6 rounded-md border border-black/5 bg-white p-5" data-testid="portfolio-burndown">
          <div className="flex items-center justify-between">
            <div>
              <p className="ek-eyebrow">Portfolio burn-down</p>
              <h3 className="font-display text-lg text-ek-ink">Total residual exposure over time</h3>
            </div>
            <p className="text-right text-xs text-ek-ink/65">
              {burndown.plan_count} plans · {burndown.current_total}/{burndown.baseline_total} → target {burndown.target_total}
            </p>
          </div>
          <div className="mt-3">
            <BurndownChart series={aggSeries} target={burndown.target_total} height={280} label="Total residual" />
          </div>
        </div>
      )}

      {/* Plans */}
      <div className="mt-6 flex items-center justify-between">
        <h3 className="font-display text-lg text-ek-ink">Remediation plans</h3>
        {canWrite && (
          <button onClick={() => setCreating(true)} data-testid="new-plan-button" className="ek-btn flex items-center gap-1.5">
            <Plus size={15} /> New plan
          </button>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {loading ? (
          <p className="py-12 text-center text-sm text-ek-ink/65">Loading plans…</p>
        ) : (data?.items || []).length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-black/5 bg-white py-16 text-ek-ink/65">
            <Target size={32} className="text-ek-purple/40" />
            <p className="text-sm">No remediation plans yet.</p>
            {canWrite && <p className="text-xs">Open a plan to assign an owner and drive a risk down.</p>}
          </div>
        ) : (
          data.items.map((p) => (
            <PlanCard key={p.plan_id} plan={p} canWrite={canWrite} owners={owners} onChanged={load} />
          ))
        )}
      </div>

      {/* New plan dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="new-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New remediation plan</DialogTitle>
            <DialogDescription>Assign an accountable owner and a target residual score with a due date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ek-ink/65">Risk</label>
              <Select value={form.risk_id} onValueChange={(v) => setForm({ ...form, risk_id: v, target_residual: "" })}>
                <SelectTrigger data-testid="plan-risk-select"><SelectValue placeholder="Select a risk" /></SelectTrigger>
                <SelectContent>
                  {risks.filter((r) => r.residual_score > 1 && r.status !== "closed").map((r) => (
                    <SelectItem key={r.risk_id} value={r.risk_id}>{r.title} (residual {r.residual_score})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-ek-ink/65">Owner</label>
                <Select value={form.owner_user_id} onValueChange={(v) => setForm({ ...form, owner_user_id: v })}>
                  <SelectTrigger data-testid="plan-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>
                    {owners.map((o) => <SelectItem key={o.user_id} value={o.user_id}>{o.name} · {o.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-ek-ink/65">
                  Target residual{selectedRisk ? ` (< ${selectedRisk.residual_score})` : ""}
                </label>
                <input type="number" min="1" max={selectedRisk ? selectedRisk.residual_score - 1 : 24}
                  value={form.target_residual} onChange={(e) => setForm({ ...form, target_residual: e.target.value })}
                  data-testid="plan-target-input"
                  className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-ek-ink/65">Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="plan-due-input"
                className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple" />
            </div>
            <div>
              <label className="text-xs font-semibold text-ek-ink/65">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} placeholder="Remediation approach…" data-testid="plan-desc-input"
                className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={submit} disabled={saving} data-testid="create-plan-submit" className="ek-btn w-full">
              {saving ? "Creating…" : "Create plan"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
